import { useEffect, useState } from "react";
import { useBlocker } from "react-router";
import { useIntl } from "react-intl";
import { usePendingEdits } from "@/lib/pending-edits";
import { localizedMessageOf } from "@/lib/localized-error";
import { logApplicationFailure } from "@/lib/user-error";
import { UnsavedChangesDialog } from "@/components/common/UnsavedChangesDialog";

// Protects against data loss: warns about unsaved inline edits before the
// user navigates (in-app) or closes/reloads the tab. Dirty state comes from
// the central PendingEdits store.
//
// This one owns the NAVIGATION occasion and nothing else. An action that destroys edits without
// moving the URL — changing the acting-company context, which empties the Apollo cache and remounts
// the editors under it — never reaches a router blocker, so it holds itself through
// lib/unsaved-edits-gate.ts and renders the same dialog.

export function UnsavedGuard() {
  const intl = useIntl();
  const ctx = usePendingEdits();
  const dirtyScopes = Object.values(ctx?.scopes ?? {}).filter((s) => s.dirty);
  const hasDirty = dirtyScopes.length > 0;

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Tab close / reload → native browser warning.
  useEffect(() => {
    if (!hasDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasDirty]);

  // In-app navigation blocked while dirty.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasDirty && currentLocation.pathname !== nextLocation.pathname,
  );

  // A BLOCKER THAT FIRED IS NOT A BLOCKER THAT STILL APPLIES. `hasDirty` reaches the router one
  // commit late: the predicate is (re)registered from an effect, so a page that clears its own
  // scope and then navigates — which is exactly what a form does the moment its write lands — is
  // stopped by a dirty state that no longer exists. Nothing would be lost by proceeding and there
  // is nothing to ask about, so the navigation is let through rather than answered with a dialog
  // listing no edits.
  //
  // This can never release a navigation that ought to be held: it only fires when NO scope is
  // dirty, and a held navigation with nothing dirty is the dialog asking about nothing.
  useEffect(() => {
    if (blocker.state === "blocked" && !hasDirty) blocker.proceed?.();
  }, [blocker, hasDirty]);

  const onStay = () => {
    setError("");
    blocker.reset?.();
  };

  const onDiscard = () => {
    setError("");
    dirtyScopes.forEach((s) => s.discard());
    blocker.proceed?.();
  };

  const onSave = async () => {
    setSaving(true);
    setError("");
    try {
      await Promise.all(dirtyScopes.map((s) => s.save()));
      ctx?.signalSaved();
      blocker.proceed?.();
    } catch (e) {
      // Only a LocalizedError's message reaches the screen — same rule and same reasoning as
      // unsaved-edits-gate.ts, which this path predates.
      const localized = localizedMessageOf(e);
      if (!localized) logApplicationFailure("unsaved-guard save");
      setError(
        localized ?? intl.formatMessage({ id: "common.saveFailed", defaultMessage: "Save failed." }),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <UnsavedChangesDialog
      id="unsaved-guard"
      open={blocker.state === "blocked"}
      saving={saving}
      error={error}
      onStay={onStay}
      onDiscard={onDiscard}
      onSave={() => void onSave()}
    />
  );
}
